import type { AiCost } from '../ai/ui/CostTag';
import { apiRequest } from '@/lib/api-client';
import { env } from '@/config/env';

export type VideoStatus =
  | 'queued'
  | 'scripting'
  | 'generating_media'
  | 'assembling'
  | 'ready'
  | 'failed';

export interface VideoScene {
  index: number;
  narration: string;
  imagePrompt: string;
  imageUrl: string | null;
  audioUrl: string | null;
  durationSec: number | null;
}

export type PublishPlatform = 'youtube' | 'tiktok' | 'meta';

export interface VideoPublishRef {
  platform: PublishPlatform;
  ref: string;
  url: string | null;
  publishedAt: string;
}

export interface VideoPart {
  index: number;
  label: string;
  videoUrl: string;
  durationSec: number | null;
  published: VideoPublishRef[];
}

export interface FacelessVideo {
  id: string;
  title: string;
  topic: string;
  language: string;
  targetSeconds: number;
  visualMode: VideoVisualMode;
  status: VideoStatus;
  scenes: VideoScene[];
  description: string;
  hashtags: string[];
  tags: string[];
  thumbnailUrl: string | null;
  videoUrl: string | null;
  parts: VideoPart[];
  published: VideoPublishRef[];
  publishPendingUntil: string | null;
  error: string | null;
  cost: AiCost | null;
  createdAt: string;
}

export interface BulkPublishResult {
  video: FacelessVideo;
  results: { target: string; ok: boolean; error?: string }[];
}

export type PublishAllResponse =
  | ({ scheduled: false } & BulkPublishResult)
  | {
      scheduled: true;
      count: number;
      intervalMinutes: number;
      firstAt: string | null;
      lastAt: string | null;
    };

const base = (orgId: string, projectId: string): string =>
  `/api/v1/organizations/${orgId}/projects/${projectId}/videos`;

export const videoApi = {
  list: (orgId: string, projectId: string) => apiRequest<FacelessVideo[]>(base(orgId, projectId)),
  get: (orgId: string, projectId: string, id: string) =>
    apiRequest<FacelessVideo>(`${base(orgId, projectId)}/${id}`),
  create: (
    orgId: string,
    projectId: string,
    body: { topic: string; language: string; targetSeconds: number },
  ) => apiRequest<FacelessVideo>(base(orgId, projectId), { method: 'POST', body }),
  publish: (
    orgId: string,
    projectId: string,
    id: string,
    platform: PublishPlatform,
    partIndex?: number,
  ) =>
    apiRequest<FacelessVideo>(`${base(orgId, projectId)}/${id}/publish`, {
      method: 'POST',
      body: { platform, ...(partIndex !== undefined ? { partIndex } : {}) },
    }),
  retry: (orgId: string, projectId: string, id: string) =>
    apiRequest<FacelessVideo>(`${base(orgId, projectId)}/${id}/retry`, { method: 'POST' }),
  remove: (orgId: string, projectId: string, id: string) =>
    apiRequest<{ deleted: boolean }>(`${base(orgId, projectId)}/${id}`, { method: 'DELETE' }),
  publishAll: (orgId: string, projectId: string, id: string, intervalMinutes?: number) =>
    apiRequest<PublishAllResponse>(`${base(orgId, projectId)}/${id}/publish-all`, {
      method: 'POST',
      body: { ...(intervalMinutes ? { intervalMinutes } : {}) },
    }),
  getChannel: (orgId: string, projectId: string) =>
    apiRequest<VideoChannel>(`${base(orgId, projectId)}/channel`),
  updateChannel: (orgId: string, projectId: string, body: Partial<VideoChannel>) =>
    apiRequest<VideoChannel>(`${base(orgId, projectId)}/channel`, { method: 'PATCH', body }),
  /**
   * Uploads the channel's background clip as a raw body. Replaced a pasted URL:
   * a link only works if it points at raw video bytes, and a YouTube page serves
   * HTML, which failed deep inside ffmpeg long after the user had moved on.
   */
  uploadBackground: (
    orgId: string,
    projectId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<VideoChannel> =>
    new Promise<VideoChannel>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      // See moviereview.api.ts — a relative upload URL bypasses VITE_API_URL
      // and forces the request through the frontend proxy.
      xhr.open('POST', `${env.apiUrl}${base(orgId, projectId)}/channel/background`);
      xhr.withCredentials = true;
      xhr.setRequestHeader('content-type', file.type || 'video/mp4');
      xhr.setRequestHeader(
        'x-csrf-token',
        document.cookie
          .split('; ')
          .find((c) => c.startsWith('csrf_token='))
          ?.slice('csrf_token='.length) ?? '',
      );
      // A proxy that refuses the body past its size limit simply stops reading:
      // the browser's send buffer fills, onprogress freezes, and the request
      // hangs forever with no error at all. Surface that rather than spinning —
      // the usual cause is a proxy request-size limit (client_max_body_size).
      let lastLoaded = 0;
      let lastMoved = Date.now();
      const stallCheck = setInterval(() => {
        if (Date.now() - lastMoved > 30_000) {
          clearInterval(stallCheck);
          stalled = true;
          xhr.abort();
        }
      }, 5000);
      let stalled = false;
      const done = (): void => clearInterval(stallCheck);
      xhr.upload.onprogress = (e) => {
        if (e.loaded !== lastLoaded) {
          lastLoaded = e.loaded;
          lastMoved = Date.now();
        }
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        done();
        let payload: { data?: VideoChannel; error?: { message?: string } } = {};
        try {
          payload = JSON.parse(xhr.responseText) as typeof payload;
        } catch {
          // Non-JSON body — fall through to the status code.
        }
        if (xhr.status >= 200 && xhr.status < 300 && payload.data) resolve(payload.data);
        else reject(new Error(payload.error?.message ?? `Upload failed (HTTP ${xhr.status})`));
      };
      xhr.onerror = () => {
        done();
        reject(new Error('Upload failed — check your connection'));
      };
      xhr.onabort = () => {
        done();
        reject(
          new Error(
            stalled
              ? 'Upload stalled — the server stopped accepting data, usually a proxy request-size limit.'
              : 'Upload cancelled',
          ),
        );
      };
      xhr.send(file);
    }),
  runAutopilot: (orgId: string, projectId: string) =>
    apiRequest<{ taskId: string }>(`${base(orgId, projectId)}/autopilot`, { method: 'POST' }),
};

export interface ChannelKit {
  names: string[];
  handle: string;
  bio: string;
  about: string;
  keywords: string[];
  avatarUrl: string | null;
  bannerUrl: string | null;
  updatedAt: string | null;
}

const projectBase = (orgId: string, projectId: string): string =>
  `/api/v1/organizations/${orgId}/projects/${projectId}`;

export interface ChannelKitApplyResult {
  platform: 'youtube' | 'meta';
  applied: string[];
  warnings: string[];
}

export const channelKitApi = {
  get: (orgId: string, projectId: string) =>
    apiRequest<ChannelKit | null>(`${projectBase(orgId, projectId)}/channel-kit`),
  generate: (orgId: string, projectId: string) =>
    apiRequest<ChannelKit>(`${projectBase(orgId, projectId)}/channel-kit/generate`, {
      method: 'POST',
    }),
  apply: (orgId: string, projectId: string, platform: 'youtube' | 'meta') =>
    apiRequest<ChannelKitApplyResult>(`${projectBase(orgId, projectId)}/channel-kit/apply`, {
      method: 'POST',
      body: { platform },
    }),
};

/**
 * Where a video's picture comes from. `generated` draws a scene image per beat;
 * `stock_loop` narrates over one continuous background clip ("did you know").
 */
export type VideoVisualMode = 'generated' | 'stock_loop';

export interface VideoChannel {
  theme: string;
  language: string;
  targetSeconds: number;
  videosPerRun: number;
  visualMode: VideoVisualMode;
  /** Publicly fetchable background clip, required by `stock_loop`. */
  backgroundUrl: string;
  autoPublish: boolean;
  /** Daily publish time "HH:mm" in publishTimezone. */
  publishTime: string;
  publishTimezone: string;
}
